import { buyWithTon } from "../utils/purchaseHelpers";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";
import WebApp from "@twa-dev/sdk";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { toast } from "react-toastify";
import { FaGem, FaTicketAlt, FaStar, FaExclamationCircle } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import "react-toastify/dist/ReactToastify.css";
import { useTitle } from "../hooks/useTitle";
import ReactGA from "react-ga4";
import AdSense from "react-adsense";
import { motion } from "framer-motion";
import { useUser } from "../context/UserContext";
import LiquidGlass from "../components/LiquidGlass";
import haptic from "../utils/haptic";
import ProductCardSkeleton from "../components/ProductCardSkeleton";
import { checkPaymentStatusWithRetry, requestInvoiceLink } from "../utils/paymentHelpers";

const Store = () => {
  useTitle("Store");
  const { userData, APIKey, refreshUser } = useUser();
  const [loading, setLoading] = useState({});
  const [storeItems, setStoreItems] = useState({ gems: [], products: [] });
  const [dataLoading, setDataLoading] = useState(true);
  const [tonConnectUI] = useTonConnectUI();
  const [tonReceiverAddress, setTonReceiverAddress] = useState(null);

  // Fetch store items from API
  useEffect(() => {
    const fetchStoreItems = async () => {
      // Fetch TON wallet config non-blocking — a failure here won't hide store items
      try {
        const configRes = await axios.get(`${API_URL}/api/store/ton-config`, {
          headers: { "x-api-key": APIKey },
        });
        setTonReceiverAddress(configRes.data.tonAddress);
      } catch (err) {
        console.warn("Could not fetch TON config:", err.message);
      }

      // Fetch store items independently
      try {
        const response = await axios.get(`${API_URL}/api/store`, {
          headers: { "x-api-key": APIKey },
        });
        if (response.data && response.data.length > 0) {
          const fetchedGems = response.data
            .filter((item) => item.type === "gems")
            .map((item) => ({ gems: item.amount, stars: item.stars, tonPrice: item.tonPrice, _id: item._id }));
          const fetchedProducts = response.data
            .filter((item) => item.type === "tickets")
            .map((item) => ({ tickets: item.amount, stars: item.stars, tonPrice: item.tonPrice, _id: item._id }));
          
          setStoreItems({
            gems: fetchedGems,
            products: fetchedProducts,
          });
        }
      } catch (error) {
        console.error("Failed to fetch store items:", error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchStoreItems();
  }, []);

  const products = storeItems.products;
  const gems = storeItems.gems;

  const handlePurchase = async (gemsAmount, ticketsAmount, starsAmount, itemId) => {
    haptic.tap();
    ReactGA.event({
      category: "Purchase",
      action: "Purchase initiated",
      label: "Store",
      items: [
        { name: "Tickets", quantity: ticketsAmount, stars: starsAmount },
        { name: "Gems", quantity: gemsAmount, stars: starsAmount },
      ],
    });
    setLoading((prev) => ({ ...prev, [itemId]: true }));

    try {
      const initData = window.Telegram.WebApp.initData;
      const user = userData.user || userData;

      const { invoiceLink, orderId } = await requestInvoiceLink(
        user.userId || user.id,
        { gems: gemsAmount, tickets: ticketsAmount, stars: starsAmount, itemId, _id: null },
        APIKey
      );

      const cleanInvoiceLink = invoiceLink ? invoiceLink.replace(/"/g, '').trim() : "";
      WebApp.openInvoice(cleanInvoiceLink, async (status) => {
        let isPaid = false;
        if (status !== "cancelled") {
          isPaid = await checkPaymentStatusWithRetry(
            user.userId || user.id,
            orderId,
            APIKey,
            7,
            2000
          );
        }

        if (isPaid) {
          toast.success("Payment successful! Your items will be added shortly.");
          haptic.success();

          // Poll until the bot credits land (up to 5 attempts × 3s)
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            await refreshUser();
            if (attempts >= 5) clearInterval(poll);
          }, 3000);
        } else if (status === "cancelled") {
          ReactGA.event({
            category: "Purchase",
            action: "Purchase Cancelled",
            label: "Store",
          });
          toast.error("Payment was cancelled.");
          haptic.error();
        } else {
          toast.info(`Payment is processing. Resume in Profile → Purchases. Order: ${orderId}. Do not pay again.`);
          haptic.error();
        }
      });
    } catch (error) {
      console.error("Error handling purchase:", error);
      toast.error(
        error?.response?.data?.error
          ? `Payment failed: ${error.response.data.error}`
          : error?.message
          ? `Payment failed: ${error.message}`
          : `Payment failed: ${JSON.stringify(error)}`,
        {
          position: "bottom-center",
          autoClose: 2000,
        }
      );
      haptic.error();
    } finally {
      setLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleTonPurchase = async (gemsAmount, ticketsAmount, tonAmount, itemId) => {
    haptic.tap();
    setLoading((prev) => ({ ...prev, [itemId]: true }));

    try {
      if (!tonConnectUI.connected) {
        toast.info("Please explicitly connect your TON wallet in the Profile tab first!");
        tonConnectUI.openModal();
        setLoading((prev) => ({ ...prev, [itemId]: false }));
        return;
      }

      if (!tonReceiverAddress) {
        toast.error("Developer TON receiver address not configured.");
        setLoading((prev) => ({ ...prev, [itemId]: false }));
        return;
      }

      const user = userData.user || userData;
      const result = await buyWithTon({ itemId }, user.userId || user.id, APIKey, tonConnectUI);
      const verifyRes = { data: { success: result.state === "fulfilled" } };
      if (!verifyRes.data.success) {
        toast.info(`Payment pending. Resume in Profile → Purchases. Order: ${result.orderId}. Do not pay again.`);
        return;
      }

      if (verifyRes.data.success) {
        toast.success("TON Payment successful! Items added securely.");
        haptic.success();
        
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          await refreshUser();
          if (attempts >= 5) clearInterval(poll);
        }, 3000);
      } else {
        toast.error("Payment verification failed on the blockchain.");
      }
    } catch (error) {
      console.error("TON Purchase error:", error);
      if (error?.message?.includes("Reject")) {
        toast.error("Transaction rejected by user.");
      } else {
        toast.error("Failed to execute TON transaction.");
      }
      haptic.error();
    } finally {
      setLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const ProductCard = ({ title, items, icon, description }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center">
              {icon}
              <span className="ml-3">{title}</span>
            </h2>
            {description && (
              <p className="text-slate-600 dark:text-gray-400 text-sm mt-1">{description}</p>
            )}
          </div>
        </div>
        <LiquidGlass
          className="p-4 rounded-xl items-center"
          isAnimating={true}
          style={{ filter: "none" }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mx-auto place-items-center">
            {items.map((item, index) => {
              const amount = item.gems || item.tickets;
              const isGems = !!item.gems;
              const itemId = item._id;
              
              return (
                <div key={index} className="flex flex-col gap-2 w-full max-w-44 min-w-36">
                  {/* Item Display Card */}
                  <div className="bg-white w-full dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl p-6 flex flex-col items-center justify-center mb-1 shadow-sm">
                    <div className="text-3xl font-bold text-slate-800 dark:text-white flex items-center justify-center w-full gap-2">
                      {amount}{" "}
                      {isGems ? (
                        <FaGem className="text-amber-400 drop-shadow-md" size={28} />
                      ) : (
                        <FaTicketAlt className="text-red-400 drop-shadow-md" size={28} />
                      )}
                    </div>
                  </div>

                  {/* Primary Stars Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePurchase(item.gems || 0, item.tickets || 0, item.stars, itemId)}
                    disabled={loading[itemId]}
                    className="bg-[#2AABEE]/10 w-full text-[#2AABEE] border border-[#2AABEE]/30 rounded-xl py-2.5 px-3 flex flex-col items-center justify-center transition duration-300 hover:bg-[#2AABEE]/20 disabled:opacity-50"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-[11px] font-bold tracking-wide uppercase">Stars</span>
                      {loading[itemId] ? (
                        <ImSpinner2 className="animate-spin text-[#2AABEE]" />
                      ) : (
                        <span className="flex items-center text-sm font-bold">
                          {item.stars} <FaStar className="ml-1 text-amber-500" size={14} />
                        </span>
                      )}
                    </div>
                  </motion.button>
                  
                  {/* Secondary Crypto Button */}
                  {item.tonPrice && item.tonPrice > 0 ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleTonPurchase(item.gems || 0, item.tickets || 0, item.tonPrice, itemId)}
                      disabled={loading[itemId]}
                      className="bg-[#0088cc] w-full text-white rounded-xl py-2.5 px-3 flex flex-col items-center justify-center transition duration-300 hover:bg-[#0077b3] disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="text-[11px] font-bold tracking-wide uppercase text-white/90">Crypto</span>
                        {loading[itemId] ? (
                          <ImSpinner2 className="animate-spin text-white" size={12} />
                        ) : (
                          <span className="flex items-center text-sm font-bold">
                            {item.tonPrice} TON
                          </span>
                        )}
                      </div>
                    </motion.button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </LiquidGlass>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="bg-slate-50 dark:bg-gradient-to-b dark:from-[#0f172a] dark:via-[#1e293b] dark:to-[#0f172a] text-slate-900 dark:text-white min-h-screen pb-24 relative overflow-hidden transition-colors duration-300"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto pt-20 pb-6 px-4"
      >


        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-amber-100/50 dark:bg-yellow-500/10 border border-amber-300 dark:border-yellow-400 text-amber-700 dark:text-yellow-300 text-sm font-semibold px-4 py-3 rounded-xl shadow mb-6 text-center"
        >
          <FaExclamationCircle className="inline mr-2" />
          All purchases are final and non-refundable.
        </motion.div>

        <section>
          {dataLoading ? (
            <ProductCardSkeleton />
          ) : gems && gems.length > 0 ? (
            <ProductCard
              icon={<FaGem className="text-amber-400" size={70} />}
              title="Buy Gems"
              description="For channel invite links"
              items={gems}
            />
          ) : null}
        </section>

        <AdSense.Google
          client="ca-pub-4252052358641832"
          slot="6502669487"
          style={{ display: "block", margin: "2rem 0" }}
          format="auto"
          responsive="true"
        />

        <section>
          {dataLoading ? (
            <ProductCardSkeleton />
          ) : products && products.length > 0 ? (
            <ProductCard
              icon={<FaTicketAlt className="text-red-400" size={70} />}
              title="Buy Tickets"
              description="For lucky spins"
              items={products}
            />
          ) : null}
        </section>
      </motion.div>
    </motion.div>
  );
};

export default Store;
